'use client'

import React, { useEffect, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface FullscreenProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  actions?: ReactNode
  className?: string
  headerClassName?: string
  contentClassName?: string
  showCloseButton?: boolean
  escToClose?: boolean
  lockBodyScroll?: boolean
  enableViewportFix?: boolean
  children: React.ReactNode
}

/**
 * A reusable fullscreen overlay container with header, body, and built-in
 * ESC-to-close, body scroll lock, and mobile viewport fixes.
 */
export function Fullscreen({
  open,
  onClose,
  title,
  actions,
  className,
  headerClassName,
  contentClassName,
  showCloseButton = false,
  escToClose = true,
  lockBodyScroll = true,
  enableViewportFix = true,
  children,
}: FullscreenProps) {
  useEffect(() => {
    if (!open) return

    const handleKey = (e: KeyboardEvent) => {
      if (escToClose && e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKey)

    // Body scroll lock + mobile viewport optimizations
    const body = document.body
    const originalOverflow = window.getComputedStyle(body).overflow
    const originalPosition = window.getComputedStyle(body).position
    const originalWidth = body.style.width
    const originalHeight = body.style.height

    if (lockBodyScroll) {
      body.style.overflow = 'hidden'
      body.style.position = 'fixed'
      body.style.width = '100%'
      body.style.height = '100%'
    }

    let viewportMeta: HTMLMetaElement | null = null
    let originalViewport = ''
    if (enableViewportFix) {
      viewportMeta = document.querySelector('meta[name="viewport"]')
      originalViewport = viewportMeta?.getAttribute('content') || ''
      if (viewportMeta) {
        viewportMeta.setAttribute(
          'content',
          'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover',
        )
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKey)
      if (lockBodyScroll) {
        body.style.overflow = originalOverflow || ''
        body.style.position = originalPosition || ''
        body.style.width = originalWidth || ''
        body.style.height = originalHeight || ''
      }
      if (enableViewportFix && viewportMeta) {
        viewportMeta.setAttribute('content', originalViewport)
      }
    }
  }, [open, escToClose, lockBodyScroll, enableViewportFix, onClose])

  if (!open) return null

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex flex-col bg-background text-foreground',
        className,
      )}
      style={{ height: '100dvh' }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          'flex flex-shrink-0 items-center justify-between border-b border-border bg-card p-2 shadow-sm md:p-4',
          headerClassName,
        )}
      >
        <div className="flex min-w-0 items-center gap-1 md:gap-2">{title}</div>
        <div className="flex items-center gap-1 md:gap-2">
          {actions}
          {showCloseButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0 text-muted-foreground hover:bg-muted md:h-8 md:w-8"
              title="Close (ESC)"
            >
              <X className="h-3 w-3 md:h-4 md:w-4" />
            </Button>
          )}
        </div>
      </div>
      <div
        className={cn(
          'min-h-0 flex-1 overflow-auto p-2 md:p-6',
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  )
}

export default Fullscreen
